import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import classNames from "classnames";
import debounce from "lodash.debounce";
import { useMemo, useEffect, useRef, useState } from "react";
const KEY_ALL = "all";
const buildMaps = (parentMap, nodeMap, nodes, parent = null) => {
  var _a;
  for (const node of nodes) {
    nodeMap.set(node.key, node);
    if (parent)
      parentMap.set(node.key, parent);
    if ((_a = node.children) == null ? void 0 : _a.length) {
      buildMaps(parentMap, nodeMap, node.children, node);
    }
  }
  return [parentMap, nodeMap];
};
const filterTreeBySearchQuery = (tree, searchQuery) => {
  if (!searchQuery)
    return { items: tree };
  const lowerQuery = searchQuery.toLowerCase();
  let count = 0;
  const filterNode = (node) => {
    var _a;
    const isMatch = node.label.toLowerCase().includes(lowerQuery);
    const filteredChildren = ((_a = node.children) == null ? void 0 : _a.map(filterNode).filter((node2) => node2 !== null)) || [];
    if (isMatch || filteredChildren.length > 0) {
      count++;
      return {
        ...node,
        children: filteredChildren.length > 0 ? filteredChildren : void 0
      };
    }
    return null;
  };
  return {
    items: tree.map(filterNode).filter(
      (n) => n !== null
    ),
    count
  };
};
function escapeRegExp(str) {
  return String(str).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
function highlightMatch(label, searchQuery) {
  if (!searchQuery || String(searchQuery).trim() === "")
    return label;
  const rawQuery = String(searchQuery).trim();
  const safeQuery = escapeRegExp(rawQuery);
  const regex = new RegExp(`(${safeQuery})`, "i");
  const labelParts = String(label).split(regex);
  const lowerQuery = rawQuery.toLowerCase();
  return labelParts.map(
    (labelPart, index) => labelPart.toLowerCase() === lowerQuery ? /* @__PURE__ */ jsx("span", { className: "highlighted", children: labelPart }, index) : labelPart
  );
}
function Label({ id, label, searchQuery, onSelectItem, labelId, isSelected, isParent }) {
  const labelHighlighted = useMemo(() => highlightMatch(label, searchQuery), [label, searchQuery]);
  const handleKeyDown = (event) => {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      onSelectItem(id);
    }
  };
  return /* @__PURE__ */ jsx(
    "span",
    {
      role: "option",
      "aria-selected": !!isSelected,
      className: classNames("option-button label", { parent: isParent }),
      onClick: (e) => {
        e.stopPropagation();
        onSelectItem(id);
      },
      onKeyDown: handleKeyDown,
      tabIndex: 0,
      id: labelId ?? `label-${id}`,
      children: labelHighlighted
    }
  );
}
const OpenerIcon = "data:image/svg+xml,%3csvg%20width='6'%20height='12'%20viewBox='0%200%206%2012'%20fill='none'%20xmlns='http://www.w3.org/2000/svg'%3e%3cpath%20d='M5.88617%205.72409L0.552834%200.591423C0.337306%200.384005%20-4.76837e-07%200.552348%20-4.76837e-07%200.867332V11.1327C-4.76837e-07%2011.4477%200.337306%2011.616%200.552834%2011.4086L5.88617%206.27591C6.03794%206.12984%206.03794%205.87016%205.88617%205.72409Z'%20fill='%239AA1A8'/%3e%3c/svg%3e";
function OpenButton({ id, isOpened, onToggle }) {
  const className = classNames({
    collapseBtn: true,
    opened: isOpened
  });
  const handleKeyDown = (event) => {
    if (event.key === " ") {
      onToggle(id);
    }
  };
  return /* @__PURE__ */ jsx(
    "div",
    {
      className,
      role: "button",
      onClick: () => onToggle(id),
      onKeyDown: handleKeyDown,
      tabIndex: 0,
      children: /* @__PURE__ */ jsx("img", { src: OpenerIcon, alt: "Toggle list" })
    }
  );
}
function ListItem({
  idx,
  listItem,
  isNested,
  searchQuery,
  openedItems,
  selectedItem,
  onToggleItem,
  onSelectItem
}) {
  const { label, children } = listItem;
  const id = listItem.key ?? listItem.label;
  const isTopLevel = id === KEY_ALL;
  const isOpened = openedItems.includes(id) || isTopLevel;
  const isHidden = isTopLevel && searchQuery && isNested;
  const hasChildren = Boolean(children && children.length > 0);
  const className = classNames({
    row: true,
    parent: hasChildren || isTopLevel
  });
  const isNeedOpenButton = hasChildren;
  const getNextElementForFocus = (pointer) => {
    const listItems = Array.from(document.querySelectorAll(".option-button"));
    const currentIndex = listItems.findIndex((el) => el === document.activeElement);
    const nextIndex = (currentIndex + pointer + listItems.length) % listItems.length;
    return listItems[nextIndex];
  };
  const handleArrowKeys = (e, isOpened2, hasChildren2, id2, idx2, onToggleItem2) => {
    if (e.key === "ArrowRight" && !isOpened2) {
      onToggleItem2(id2);
    } else if (e.key === "ArrowLeft" && isOpened2) {
      onToggleItem2(id2);
    }
    if (e.key === "ArrowRight" && isOpened2 && hasChildren2 && id2 !== KEY_ALL || e.key === "ArrowDown") {
      const el = getNextElementForFocus(1);
      el.focus();
    } else if (e.key === "ArrowUp" || e.key === "ArrowLeft" && !isOpened2) {
      const el = getNextElementForFocus(-1);
      el.focus();
    }
  };
  const handleKeyDown = (e) => {
    if (["ArrowRight", "ArrowLeft", "ArrowUp", "ArrowDown"].includes(e.key)) {
      e.stopPropagation();
      e.preventDefault();
      handleArrowKeys(e, isOpened, hasChildren, id, idx, onToggleItem);
    }
  };
  const labelId = `searchabledropdown-list-item-label-${id}`;
  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    /* @__PURE__ */ jsxs("li", { onKeyDown: handleKeyDown, className: "searchabledropdown-list-item", children: [
      !isHidden && /* @__PURE__ */ jsxs("div", { className, children: [
        isNeedOpenButton && /* @__PURE__ */ jsx(OpenButton, { id, onToggle: onToggleItem, isOpened }),
        /* @__PURE__ */ jsx(
          Label,
          {
            id,
            label,
            searchQuery,
            onSelectItem: (clickedId) => {
              if (hasChildren || isTopLevel) {
                onToggleItem(clickedId);
              } else {
                onSelectItem(clickedId);
              }
            },
            labelId,
            isParent: hasChildren || isTopLevel
          }
        )
      ] }),
      hasChildren && isOpened && /* @__PURE__ */ jsx(
        List,
        {
          list: children,
          isNested,
          searchQuery,
          openedItems,
          selectedItem,
          onToggleItem,
          onSelectItem,
          isOpened,
          noOffset: isTopLevel
        }
      )
    ] })
  );
}
function List({
  list,
  searchQuery,
  isOpened,
  noOffset,
  openedItems,
  selectedItem,
  isNested,
  onToggleItem,
  onSelectItem
}) {
  const className = classNames({
    list: true,
    opened: isOpened,
    main: noOffset
  });
  return /* @__PURE__ */ jsx("ul", { className, children: list.map((listItem, idx) => /* @__PURE__ */ jsx(
    ListItem,
    {
      idx,
      listItem,
      isNested,
      searchQuery,
      openedItems,
      selectedItem,
      onToggleItem,
      onSelectItem
    },
    listItem.key
  )) });
}
function Popup({
  list,
  searchQuery,
  openedItems,
  selectedItem,
  isNested,
  onToggleItem,
  onSelectItem,
  onClickOverlay
}) {
  useEffect(() => {
    document.addEventListener("mousedown", onClickOverlay);
    return () => {
      document.removeEventListener("mousedown", onClickOverlay);
    };
  }, []);
  const emptyListMessage = list.length === 0 ? searchQuery ? "No results found for search term" : "No available options" : "";
  return /* @__PURE__ */ jsx("div", { className: "popup", role: "listbox", children: emptyListMessage ? /* @__PURE__ */ jsx("span", { className: "emptyListMessage", children: emptyListMessage }) : /* @__PURE__ */ jsx("div", { className: "tree", id: "dropdown-list", children: /* @__PURE__ */ jsx(
    List,
    {
      list,
      selectedItem,
      openedItems,
      isNested,
      onSelectItem,
      onToggleItem,
      searchQuery,
      noOffset: true,
      isOpened: true
    }
  ) }) });
}
function SearchableDropdown({
  options,
  title = "",
  selected = "",
  placeholder = "",
  isNested = false,
  disabled = false,
  autoSave = false,
  onChange,
  required
}) {
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const prevSearchQuery = useRef("");
  const [openedItems, setOpenedItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(selected ?? null);
  const [status, setStatus] = useState("");
  const [isEdited, setIsEdited] = useState(false);
  const [limitedParent, setLimitedParent] = useState(null);
  const data = useMemo(() => options, [options]);
  useEffect(() => {
    if (!isEdited) {
      setSelectedItem(selected.length > 0 ? selected : null);
    }
  }, [selected]);
  const [parentMap, nodeMap] = useMemo(() => {
    const parentMap2 = /* @__PURE__ */ new Map();
    const nodeMap2 = /* @__PURE__ */ new Map();
    return buildMaps(parentMap2, nodeMap2, data);
  }, [data]);
  const label = useMemo(() => {
    if (selectedItem) {
      const node = nodeMap.get(selectedItem);
      return (node == null ? void 0 : node.label) ?? selectedItem;
    }
    return placeholder;
  }, [selectedItem, placeholder, nodeMap]);
  useEffect(() => {
    var _a;
    if (isPopupOpen) {
      (_a = searchInputRef.current) == null ? void 0 : _a.focus();
    } else if (autoSave) {
      const prev = selected ?? null;
      if (selectedItem !== prev) {
        const node = selectedItem ? nodeMap.get(selectedItem) ?? null : null;
        onChange(node);
      }
    }
  }, [isPopupOpen]);
  const onOpenPopup = () => {
    if (disabled) {
      console.log("Dropdown is disabled, not opening.");
      return;
    }
    setIsPopupOpen(!isPopupOpen);
  };
  const notifySearchUpdated = useMemo(
    () => debounce((count) => {
      if (count === void 0) {
        setStatus("");
      } else {
        setStatus(`Select items updated, list of ${count} items`);
      }
    }, 200),
    []
  );
  const filteredTree = useMemo(() => {
    if (limitedParent) {
      const parentNode = nodeMap.get(limitedParent);
      const children = (parentNode == null ? void 0 : parentNode.children) ?? [];
      notifySearchUpdated(children.length);
      return parentNode ? [{ ...parentNode, children }] : [];
    }
    const { items, count } = filterTreeBySearchQuery(data, searchQuery);
    notifySearchUpdated(count);
    const enriched = items.map((it) => {
      const key = it.key ?? it.label;
      const full = nodeMap.get(key);
      if (full && full.children && full.children.length > 0) {
        return {
          ...full,
          // preserve any search-specific children structure if present
          children: it.children ?? full.children
        };
      }
      return it;
    });
    return enriched;
  }, [data, searchQuery, limitedParent, nodeMap]);
  const onSelectItem = (id, event) => {
    if (event && typeof event.stopPropagation === "function") {
      event.stopPropagation();
    }
    if (!isEdited)
      setIsEdited(true);
    const node = nodeMap.get(id);
    if (node && node.children && node.children.length > 0) {
      if (searchQuery) {
        setLimitedParent(id);
        setOpenedItems(
          (prev) => prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
        );
      } else {
        setLimitedParent(null);
        setOpenedItems(
          (prev) => prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
        );
      }
      return;
    }
    setSelectedItem(id);
    if (node)
      onChange(node);
    setIsPopupOpen(false);
  };
  const onToggleItem = (id) => {
    setOpenedItems(
      openedItems.includes(id) ? openedItems.filter((val) => val !== id) : [...openedItems, id]
    );
  };
  const onSearchClick = (event) => {
    event.stopPropagation();
  };
  const onSearchQueryChange = (event) => {
    setSearchQuery(event.target.value);
    setLimitedParent(null);
  };
  const onClickOverlay = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsPopupOpen(false);
      if (!searchQuery) {
        setLimitedParent(null);
      }
    }
  };
  const moveFocusToFirstNode = () => {
    const topNode = document.getElementById(`select-btn`);
    if (topNode) {
      topNode.focus();
    }
  };
  const moveFocusToLastNode = () => {
    var _a;
    const topOptions = searchQuery ? data : data[0].children;
    const lastTopNode = document.getElementById(
      `select-btn-${(_a = topOptions == null ? void 0 : topOptions.slice(-1)[0]) == null ? void 0 : _a.key}`
    );
    if (lastTopNode) {
      lastTopNode.focus();
    }
  };
  const handleKeyDown = (event) => {
    var _a;
    if (event.key === "Home") {
      moveFocusToFirstNode();
    } else if (event.key === "End") {
      moveFocusToLastNode();
    } else if (event.key === "Escape") {
      setIsPopupOpen(false);
      (_a = dropdownRef.current) == null ? void 0 : _a.focus();
    } else if (event.key === "Enter" || event.key === " " && !isPopupOpen) {
      event.preventDefault();
      onOpenPopup();
    }
  };
  useEffect(() => {
    if (limitedParent && !openedItems.includes(limitedParent)) {
      setOpenedItems(
        (prev) => prev.includes(limitedParent) ? prev : [...prev, limitedParent]
      );
    }
  }, [limitedParent]);
  useEffect(() => {
    if (limitedParent)
      return;
    const prevValue = prevSearchQuery.current;
    const searchChanged = prevValue !== searchQuery;
    const wasSearchCleared = prevValue && !searchQuery;
    prevSearchQuery.current = searchQuery;
    if (wasSearchCleared)
      setOpenedItems([]);
    if (!searchQuery || !searchChanged) {
      return;
    }
    const { items } = filterTreeBySearchQuery(data, searchQuery);
    const collectOpenKeys = (nodes = []) => {
      let keys = [];
      nodes.forEach((n) => {
        var _a;
        const nodeKey = n.key ?? n.label;
        if ((_a = n.children) == null ? void 0 : _a.length) {
          keys.push(nodeKey);
          keys = keys.concat(collectOpenKeys(n.children));
        }
      });
      return keys;
    };
    const keysToOpen = collectOpenKeys(items);
    setOpenedItems(keysToOpen);
  }, [searchQuery, data, limitedParent]);
  const className = classNames({
    "sa-searchable-dropdown": true,
    opened: isPopupOpen,
    disabled
  });
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    title && /* @__PURE__ */ jsxs("label", { htmlFor: `${title}-dropdown`, className: "sa-searchable-dropdown-form-label", children: [
      title,
      required && /* @__PURE__ */ jsx("span", { "aria-hidden": "true", children: "*" })
    ] }),
    required && /* @__PURE__ */ jsx("span", { className: "sa-dropdown-label-hidden", children: "required" }),
    /* @__PURE__ */ jsxs(
      "div",
      {
        className,
        ref: dropdownRef,
        role: "combobox",
        title: title || "Dropdown",
        "aria-label": title || "Dropdown",
        "aria-haspopup": "listbox",
        "aria-expanded": isPopupOpen,
        "aria-controls": "dropdown-list",
        onKeyDown: handleKeyDown,
        tabIndex: 0,
        "aria-labelledby": `${title}-dropdown-label`,
        children: [
          /* @__PURE__ */ jsx(
            "span",
            {
              "aria-atomic": "true",
              className: "sa-searchable-dropdown-status--hidden",
              "aria-live": "polite",
              children: status
            }
          ),
          /* @__PURE__ */ jsxs(
            "div",
            {
              className: "inputBox",
              role: "button",
              onClick: onOpenPopup,
              "data-testid": "dropdown-input-box",
              children: [
                isPopupOpen ? /* @__PURE__ */ jsx(
                  "input",
                  {
                    className: "searchInput",
                    ref: searchInputRef,
                    type: "text",
                    value: searchQuery,
                    onClick: onSearchClick,
                    onChange: onSearchQueryChange,
                    "aria-label": "Search input box"
                  }
                ) : /* @__PURE__ */ jsx(
                  "span",
                  {
                    className: classNames("value", { "sa-selected": !!selectedItem }),
                    id: `${title}-dropdown-label`,
                    "data-testid": "dropdown-value",
                    "aria-label": label ?? "Placeholder",
                    children: label
                  }
                ),
                /* @__PURE__ */ jsx("div", { className: "openBtn" })
              ]
            }
          ),
          isPopupOpen && /* @__PURE__ */ jsx(
            Popup,
            {
              list: filteredTree,
              searchQuery,
              selectedItem,
              openedItems,
              isNested,
              onSelectItem,
              onToggleItem,
              onClickOverlay
            }
          )
        ]
      }
    )
  ] });
}
export {
  SearchableDropdown as default
};
